import React from 'react';
import { getResponsiveSize } from '../../utils/responsive';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { Calendar, Clock, MapPin } from 'lucide-react';
import globalStyles from '../../global';

/**
 * Accepts time as a string (e.g., "10:00 AM onwards" or "10:00 AM - 5:00 PM")
 * OR as an object { start: "10:00 AM", end: "5:00 PM" }
 */
const EventDetails = ({
  time = '10:00 AM onwards',
  date = '4th to 6th June 2025',
  location = 'BITEC Bangkok',
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(min-width:601px) and (max-width:900px)');

  // Helper to display time in flexible format
  const renderTime = (time) => {
    if (typeof time === "string") {
      return time;
    }
    if (typeof time === "object" && time !== null) {
      // Accepts {start, end} or {start, onwards: true}
      const { start, end, onwards } = time;
      if (start && end) return `${start} – ${end}`;
      if (start && onwards) return `${start} onwards`;
      if (start) return start;
      if (end) return end;
    }
    return "";
  };

  return (
    <Box sx={styles.box2}>
      <Stack sx={styles.infoStack}>
        <Stack
          direction="row"
          sx={[
            styles.infoItem,
            { justifyContent: isMobile ? 'center' : 'flex-start' },
          ]}
        >
          <Calendar
            size={
              isMobile
                ? getResponsiveSize(28)
                : isTablet
                ? getResponsiveSize(32)
                : getResponsiveSize(38)
            }
            color={globalStyles.colors.army_green}
          />
          <Typography variant='h2' sx={styles.infoText}>{date}</Typography>
        </Stack>
        <Stack
          direction="row"
          sx={[
            styles.infoItem,
            { justifyContent: isMobile ? 'center' : 'flex-start' },
          ]}
        >
          <Clock
            size={
              isMobile
                ? getResponsiveSize(28)
                : isTablet
                ? getResponsiveSize(32)
                : getResponsiveSize(38)
            }
            color={globalStyles.colors.army_green}
          />
          <Typography sx={styles.infoText}>{renderTime(time)}</Typography>
        </Stack>
        <Stack
          direction="row"
          sx={[
            styles.infoItem,
            { justifyContent: isMobile ? 'center' : 'flex-start' },
          ]}
        >
          <MapPin
            size={
              isMobile
                ? getResponsiveSize(28)
                : isTablet
                ? getResponsiveSize(32)
                : getResponsiveSize(38)
            }
            color={globalStyles.colors.army_green}
          />
          <Typography
            sx={[
              styles.infoText,
              {
                whiteSpace: 'normal',
                textAlign: isMobile ? 'center' : 'center',
              },
            ]}
          >
            {location}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const styles = {
  box2: {
    width: {
      xs: '100%',
      sm: '100%',
      md: '100%',
      lg: '100%',
    },
    backgroundColor: globalStyles.colors.base_light,
    py: {
      xs: '2%',
      sm: '2%',
      md: '4%',
      lg: '4%',
    },
  },
  infoStack: {
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    justifyContent: {
      xs: 'center',
      sm: 'space-around',
      md: 'space-evenly',
    },
    alignItems: 'center',
    rowGap: { xs: 2, sm: 0 },
    width: '100%',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: {
      xs: '100%',
      sm: 'auto',
    },
    px: { xs: 2, sm: 1 },
  },
  infoText: {
    fontFamily: ' "Poppins", serif ',
    fontWeight: 400,
    color: globalStyles.colors.army_green,
    ml: 1.5,
    fontSize: {
      xs: getResponsiveSize(14),
      sm: getResponsiveSize(14),
      md: getResponsiveSize(16),
      lg: getResponsiveSize(18),
    },
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
};

export default EventDetails;